import React, { useState, useEffect, useRef } from 'react';
import './LanguageSelector.scss';
import { LANGUAGES, LanguageCode } from '../constants/app';

interface LanguageSelectorProps {
  currentLanguage?: LanguageCode;
  onLanguageChange?: (langCode: LanguageCode) => void;
  className?: string;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  currentLanguage = 'lt',
  onLanguageChange,
  className = ''
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(currentLanguage);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const languages = LANGUAGES;

  const handleLanguageChange = (langCode: LanguageCode) => {
    setSelectedLanguage(langCode);
    setIsDropdownOpen(false);
    onLanguageChange?.(langCode);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsDropdownOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update selected language when prop changes
  useEffect(() => {
    setSelectedLanguage(currentLanguage);
  }, [currentLanguage]);

  const currentLang = languages.find(lang => lang.code === selectedLanguage) || languages[0];

  return (
    <div className={`language-selector ${className}`} ref={dropdownRef}>
      <button 
        className="language-btn"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onKeyDown={handleKeyDown}
        aria-label="Language selector"
        aria-expanded={isDropdownOpen}
      >
        <img src={currentLang.flag} alt={`${currentLang.name} flag`} className="flag-img" />
        <span className="language-name">{currentLang.name}</span>
        <span className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
      </button>
      
      {isDropdownOpen && (
        <div className="language-dropdown" onKeyDown={handleKeyDown} role="menu">
          {languages.map(lang => (
            <button
              key={lang.code}
              className={`language-option ${selectedLanguage === lang.code ? 'active' : ''}`}
              onClick={() => handleLanguageChange(lang.code)}
              role="menuitem"
              aria-current={selectedLanguage === lang.code ? 'true' : 'false'}
            >
              <img src={lang.flag} alt={`${lang.name} flag`} className="flag-img" />
              <span>{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
